import { useState, useEffect, useCallback, useRef } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { getPaymentMethods, getPayPalConfig, capturePayPalOrder } from "../lib/api";
import { Loader2, CreditCard, Smartphone, Building2, AlertCircle, Info, RefreshCw } from "lucide-react";
import { PayPalButtons, usePayPalScriptReducer, SCRIPT_LOADING_STATE } from "@paypal/react-paypal-js";
import { PayPalRedirectCheckout } from "../components/deposit/PayPalRedirectCheckout";
import { isInAppWebView } from "../lib/inAppBrowser";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// PayPal logo SVG
const PayPalLogo = () => (
  <svg className="h-5 w-auto" viewBox="0 0 101 32" xmlns="http://www.w3.org/2000/svg">
    <path fill="#003087" d="M12.237 2.8h-7.8c-.5 0-1 .4-1.1.9L.7 24.4c-.1.4.2.7.6.7h3.7c.5 0 1-.4 1.1-.9l.7-4.2c.1-.5.6-.9 1.1-.9h2.6c5.5 0 8.6-2.6 9.4-7.8.4-2.3 0-4.1-1.1-5.3-1.2-1.4-3.4-2.2-6.6-2.2zm1 7.7c-.5 3-2.8 3-5 3h-1.3l.9-5.6c.1-.3.3-.5.6-.5h.6c1.5 0 3 0 3.7.9.5.5.6 1.2.5 2.2z"/>
    <path fill="#003087" d="M35.437 10.3h-3.7c-.3 0-.6.2-.6.5l-.2 1-.3-.4c-.8-1.2-2.6-1.6-4.4-1.6-4.1 0-7.6 3.1-8.3 7.5-.4 2.2.2 4.3 1.4 5.7 1.1 1.3 2.8 1.9 4.7 1.9 3.3 0 5.2-2.1 5.2-2.1l-.2 1c-.1.4.2.8.6.8h3.4c.5 0 1-.4 1.1-.9l2-12.6c.1-.4-.2-.8-.7-.8zm-5.2 7.3c-.4 2.1-2 3.6-4.2 3.6-1.1 0-2-.4-2.5-.9-.5-.6-.7-1.4-.6-2.3.3-2.1 2.1-3.6 4.1-3.6 1.1 0 1.9.4 2.5.9.6.6.8 1.4.7 2.3z"/>
    <path fill="#009cde" d="M55.337 10.3h-3.7c-.4 0-.7.2-.9.5l-5.3 7.7-2.2-7.4c-.1-.5-.6-.8-1.1-.8h-3.7c-.4 0-.8.4-.6.9l4.2 12.3-4 5.6c-.3.4 0 1 .5 1h3.7c.4 0 .7-.2.9-.5l12.7-18.3c.3-.5 0-1-.5-1z"/>
    <path fill="#009cde" d="M67.737 2.8h-7.8c-.5 0-1 .4-1.1.9l-2.6 20.7c-.1.4.2.7.6.7h4c.4 0 .7-.3.7-.6l.8-4.5c.1-.5.6-.9 1.1-.9h2.6c5.5 0 8.6-2.6 9.4-7.8.4-2.3 0-4.1-1.1-5.3-1.2-1.4-3.4-2.2-6.6-2.2zm1 7.7c-.5 3-2.8 3-5 3h-1.3l.9-5.6c.1-.3.3-.5.6-.5h.6c1.5 0 3 0 3.7.9.5.5.6 1.2.5 2.2z"/>
    <path fill="#009cde" d="M90.937 10.3h-3.7c-.3 0-.6.2-.6.5l-.2 1-.3-.4c-.8-1.2-2.6-1.6-4.4-1.6-4.1 0-7.6 3.1-8.3 7.5-.4 2.2.2 4.3 1.4 5.7 1.1 1.3 2.8 1.9 4.7 1.9 3.3 0 5.2-2.1 5.2-2.1l-.2 1c-.1.4.2.8.6.8h3.4c.5 0 1-.4 1.1-.9l2-12.6c.1-.4-.3-.8-.7-.8zm-5.2 7.3c-.4 2.1-2 3.6-4.2 3.6-1.1 0-2-.4-2.5-.9-.5-.6-.7-1.4-.6-2.3.3-2.1 2.1-3.6 4.1-3.6 1.1 0 1.9.4 2.5.9.6.6.8 1.4.7 2.3z"/>
    <path fill="#009cde" d="M95.137 3.4l-2.7 17c-.1.4.2.7.6.7h3.2c.5 0 1-.4 1.1-.9l2.6-20.7c.1-.4-.2-.7-.6-.7h-3.5c-.3 0-.6.2-.7.6z"/>
  </svg>
);

// Supported currencies for deposit
const DEPOSIT_CURRENCIES = ['USD', 'EUR', 'GBP', 'NGN'];

const Deposit = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('paypal');
  
  // Deposit form state
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [processing, setProcessing] = useState(false);
  
  // PayPal
  const [paypalConfig, setPayPalConfig] = useState(null);
  const [{ isPending: paypalPending, isResolved: paypalResolved, isRejected: paypalRejected }] = usePayPalScriptReducer();
  const [sdkLoadTimeout, setSdkLoadTimeout] = useState(false);
  const sdkTimeoutRef = useRef(null);
  const [inApp] = useState(() => isInAppWebView());

  // Fetch payment methods on mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await getPaymentMethods();
        setPaymentMethods(response.data);
        
        // Set default tab to first enabled method (prefer paypal)
        if (response.data.paypal) {
          setActiveTab('paypal');
        } else if (response.data.card) {
          setActiveTab('card');
        } else if (response.data.mobile_money) {
          setActiveTab('mobile_money');
        } else if (response.data.bank_transfer) {
          setActiveTab('bank_transfer');
        }
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
        // Default to paypal only
        setPaymentMethods({ paypal: true, card: false, mobile_money: false, bank_transfer: false });
      } finally {
        setMethodsLoading(false);
      }
    };
    
    fetchPaymentMethods();
  }, []);

  // Fetch PayPal config
  useEffect(() => {
    const fetchPayPalConfig = async () => {
      try {
        const response = await getPayPalConfig();
        setPayPalConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch PayPal config:', error);
      }
    };
    
    if (paymentMethods?.paypal) {
      fetchPayPalConfig();
    }
  }, [paymentMethods]);

  // SDK load timeout detection - 10 seconds
  useEffect(() => {
    // Clear any existing timeout
    if (sdkTimeoutRef.current) {
      clearTimeout(sdkTimeoutRef.current);
    }
    
    // Reset timeout state when SDK status changes
    if (paypalResolved || paypalRejected) {
      setSdkLoadTimeout(false);
      return;
    }
    
    // If SDK is pending, start timeout
    if (paypalPending && paymentMethods?.paypal) {
      sdkTimeoutRef.current = setTimeout(() => {
        if (!paypalResolved) {
          setSdkLoadTimeout(true);
        }
      }, 10000); // 10 second timeout
    }
    
    return () => {
      if (sdkTimeoutRef.current) {
        clearTimeout(sdkTimeoutRef.current);
      }
    };
  }, [paypalPending, paypalResolved, paypalRejected, paymentMethods]);

  // Retry loading PayPal SDK
  const retryPayPalLoad = useCallback(() => {
    setSdkLoadTimeout(false);
    // Force page reload to reinitialize PayPal SDK
    window.location.reload();
  }, []);

  // PayPal order creation
  const createPayPalOrder = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(amount),
          currency: currency
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create order');
      }
      
      return data.order_id;
    } catch (error) {
      toast.error(error.message || 'Failed to create PayPal order');
      throw error;
    }
  }, [amount, currency]);

  // PayPal order capture
  const onPayPalApprove = useCallback(async (data) => {
    setProcessing(true);
    try {
      const response = await capturePayPalOrder(data.orderID);
      
      if (response.data.success || response.data.status === 'success' || response.data.status === 'completed') {
        toast.success(`Successfully deposited ${currency} ${amount}!`);
        setAmount('');
      } else {
        toast.error(response.data.message || 'Payment capture failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete payment');
    } finally {
      setProcessing(false);
    }
  }, [amount, currency]);

  // Get available tabs based on enabled methods
  const getAvailableTabs = () => {
    if (!paymentMethods) return [];
    
    const tabs = [];
    if (paymentMethods.paypal) {
      tabs.push({ id: 'paypal', label: 'PayPal', icon: PayPalLogo });
    }
    if (paymentMethods.card) {
      tabs.push({ id: 'card', label: 'Card', icon: CreditCard });
    }
    if (paymentMethods.mobile_money) {
      tabs.push({ id: 'mobile_money', label: 'Mobile Money', icon: Smartphone });
    }
    if (paymentMethods.bank_transfer) {
      tabs.push({ id: 'bank_transfer', label: 'Bank Transfer', icon: Building2 });
    }
    
    return tabs;
  };

  const availableTabs = getAvailableTabs();

  // Loading state
  if (methodsLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  // No payment methods available
  if (availableTabs.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <Card className="bg-card border-border">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Deposits Temporarily Unavailable</h2>
              <p className="text-muted-foreground">
                All deposit methods are currently being maintained. Please check back shortly.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto space-y-6"
      >
        <div>
          <h1 className="text-2xl font-bold">Deposit Funds</h1>
          <p className="text-muted-foreground">Add money to your ParachPay wallet</p>
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Choose Payment Method</CardTitle>
            <CardDescription>Select how you'd like to add funds</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={`grid w-full ${availableTabs.length === 1 ? 'grid-cols-1' : availableTabs.length === 2 ? 'grid-cols-2' : availableTabs.length === 3 ? 'grid-cols-3' : 'grid-cols-4'} mb-6`}>
                {availableTabs.map(tab => (
                  <TabsTrigger 
                    key={tab.id} 
                    value={tab.id}
                    className="flex items-center gap-2"
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.id === 'paypal' ? (
                      <tab.icon />
                    ) : (
                      <tab.icon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* PayPal Tab */}
              {paymentMethods.paypal && (
                <TabsContent value="paypal" className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <Info className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        Pay securely with PayPal. You can also use a debit or credit card through PayPal.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="paypal-amount">Amount</Label>
                        <Input
                          id="paypal-amount"
                          type="number"
                          min="1"
                          step="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="0.00"
                          className="mt-1"
                          data-testid="deposit-amount"
                        />
                      </div>
                      <div>
                        <Label htmlFor="paypal-currency">Currency</Label>
                        <Select value={currency} onValueChange={setCurrency}>
                          <SelectTrigger className="mt-1" data-testid="deposit-currency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DEPOSIT_CURRENCIES.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {amount && parseFloat(amount) > 0 && inApp && (
                      <div className="pt-4">
                        <PayPalRedirectCheckout amount={amount} currency={currency} />
                      </div>
                    )}

                    {amount && parseFloat(amount) > 0 && !inApp && (
                      <div className="pt-4" data-testid="paypal-buttons-container">
                        {/* SDK Load Timeout Fallback */}
                        {(sdkLoadTimeout || paypalRejected) && (
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">
                                Having trouble loading PayPal? Please try again.
                              </p>
                            </div>
                            <Button 
                              onClick={retryPayPalLoad}
                              className="w-full bg-[#ffc439] hover:bg-[#f0b82d] text-[#003087] font-semibold"
                              data-testid="paypal-retry-btn"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Reload and Try Again
                            </Button>
                          </div>
                        )}
                        
                        {/* SDK Loading State */}
                        {paypalPending && !sdkLoadTimeout && !paypalRejected && (
                          <div className="flex flex-col items-center justify-center py-4 space-y-2">
                            <Loader2 className="h-6 w-6 animate-spin text-[#003087]" />
                            <p className="text-sm text-muted-foreground">Loading PayPal...</p>
                          </div>
                        )}
                        
                        {/* PayPal Buttons - Only render when SDK is loaded */}
                        {paypalResolved && !sdkLoadTimeout && (
                          <PayPalButtons
                            style={{ 
                              layout: "vertical",
                              color: "gold",
                              shape: "rect",
                              label: "pay"
                            }}
                            createOrder={createPayPalOrder}
                            onApprove={onPayPalApprove}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                              toast.error('PayPal encountered an error');
                            }}
                            disabled={processing || !amount || parseFloat(amount) <= 0}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>
              )}

              {/* Card Tab - Only shows if card is enabled */}
              {paymentMethods.card && (
                <TabsContent value="card" className="space-y-6">
                  <div className="space-y-4">
                    {paymentMethods.stripe_enabled && (
                      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                        <Info className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <p className="text-sm text-muted-foreground">
                          Card payments are processed securely via Stripe.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="card-amount">Amount</Label>
                        <Input
                          id="card-amount"
                          type="number"
                          min="1"
                          step="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="0.00"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="card-currency">Currency</Label>
                        <Select value={currency} onValueChange={setCurrency}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DEPOSIT_CURRENCIES.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button 
                      className="w-full" 
                      disabled={!amount || parseFloat(amount) <= 0 || processing}
                      data-testid="card-continue-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Continue to Payment
                    </Button>
                  </div>
                </TabsContent>
              )}

              {/* Mobile Money Tab - Only shows if mobile_money is enabled */}
              {paymentMethods.mobile_money && (
                <TabsContent value="mobile_money" className="space-y-6">
                  <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <Info className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Mobile Money supports M-Pesa, Airtel Money, MTN MoMo, and other African networks.
                    </p>
                  </div>
                  {/* Mobile Money form would go here */}
                  <p className="text-center text-muted-foreground py-8">
                    Mobile Money deposit interface coming soon.
                  </p>
                </TabsContent>
              )}

              {/* Bank Transfer Tab - Only shows if bank_transfer is enabled */}
              {paymentMethods.bank_transfer && (
                <TabsContent value="bank_transfer" className="space-y-6">
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                    <Info className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Bank transfers may take 1-3 business days to process.
                    </p>
                  </div>
                  {/* Bank Transfer form would go here */}
                  <p className="text-center text-muted-foreground py-8">
                    Bank Transfer deposit interface coming soon.
                  </p>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>

        {/* Deposit Info */}
        <Card className="bg-muted/30 border-border">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-3">Deposit Information</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Funds are usually available within minutes</li>
              <li>• Minimum deposit: $1.00 or equivalent</li>
              <li>• PayPal fees may apply depending on your region</li>
              <li>• Exchange rates apply for non-USD deposits</li>
            </ul>
          </CardContent>
        </Card>
      </motion.div>
    </Layout>
  );
};

export default Deposit;
